/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { gnoment } from 'shared-ui/gnoment';
import { readRuleAsMoment, readRuleAsDateString } from './utilsNew';
import * as types from '../constants';

export function parsePermitSeasonInfo(
    aggSeasonRules,
    timeZone,
    divisionId = '',
    permitRules = null
) {
    return {
        // Read as plain date strings, not timezone-converted moments, so a UTC offset
        // can't shift the season end back a day and incorrectly block the last day.
        seasonStart: readRuleAsDateString('SeasonStart', aggSeasonRules, divisionId),
        seasonEnd: readRuleAsDateString('SeasonEnd', aggSeasonRules, divisionId),
        lotterySeasonStart: readRuleAsDateString(
            'HighUseSeasonStart',
            aggSeasonRules,
            divisionId
        ),
        lotterySeasonEnd: readRuleAsDateString(
            'HighUseSeasonEnd',
            aggSeasonRules,
            divisionId
        ),
        lotteryDisplayDate: readRuleAsMoment(
            'LotteryDisplayDate',
            !permitRules ? aggSeasonRules : permitRules,
            timeZone,
            divisionId,
            true
        ),
        lotteryReleaseDate: readRuleAsMoment(
            'LotteryReleaseDate',
            !permitRules ? aggSeasonRules : permitRules,
            timeZone,
            divisionId,
            true
        ),
    };
}

export function isDayWithinSeason(day, permitSeasonInfo) {
    if (!permitSeasonInfo.seasonStart || !permitSeasonInfo.seasonEnd) {
        return false;
    }
    const dayString = day.format(types.AVAILABILITY_KEY_MAP_FORMAT);
    return (
        dayString >= permitSeasonInfo.seasonStart &&
        dayString <= permitSeasonInfo.seasonEnd
    );
}

export function isDayWithinLotterySeason(day, permitSeasonInfo) {
    if (!permitSeasonInfo.lotterySeasonStart || !permitSeasonInfo.lotterySeasonEnd) {
        return false;
    }
    const dayString = day.format(types.AVAILABILITY_KEY_MAP_FORMAT);
    return (
        dayString >= permitSeasonInfo.lotterySeasonStart &&
        dayString <= permitSeasonInfo.lotterySeasonEnd
    );
}

export function isDayManagedByLottery(day, permitSeasonInfo, currentDate) {
    return (
        isDayWithinLotterySeason(day, permitSeasonInfo) &&
        permitSeasonInfo.lotteryDisplayDate.isValid() &&
        permitSeasonInfo.lotteryReleaseDate.isValid() &&
        currentDate.isBetween(
            permitSeasonInfo.lotteryDisplayDate,
            permitSeasonInfo.lotteryReleaseDate,
            'days',
            '[]'
        )
    );
}

export function isDayBeingBlockedByLottery(
    day,
    permitSeasonInfo,
    currentDate,
    blockLotteryDates
) {
    return blockLotteryDates && isDayManagedByLottery(day, permitSeasonInfo, currentDate);
}

export function isDayBlocked(
    day,
    permitSeasonInfo,
    currentDate,
    blockNonLotteryDates,
    blockLotteryDates,
    availBools
) {
    if (day.isBefore(currentDate)) {
        return true;
    }
    if (!isDayWithinSeason(day, permitSeasonInfo)) {
        return true;
    }
    if (blockNonLotteryDates && !isDayWithinLotterySeason(day, permitSeasonInfo)) {
        return true;
    }
    if (
        isDayBeingBlockedByLottery(day, permitSeasonInfo, currentDate, blockLotteryDates)
    ) {
        return true;
    }
    if (!availBools) {
        return true;
    }
    return !availBools[day.format(types.AVAILABILITY_KEY_MAP_FORMAT)];
}

// returns first available day for either lottery or reservation based on params of avail date map and aggregate season information
export const findNextAvailableDay = (
    availBools,
    permitSeasonInfo,
    blockNonLotteryDates,
    blockLotteryDates,
    currentTime
) => {
    let nextDate = null;
    Object.keys(availBools).forEach((date) => {
        if (!nextDate) {
            if (
                !isDayBlocked(
                    gnoment(date).utc(),
                    permitSeasonInfo,
                    currentTime,
                    blockNonLotteryDates,
                    blockLotteryDates,
                    availBools
                )
            ) {
                nextDate = gnoment(date);
            }
        }
    });

    return nextDate;
};
